import * as React from "react"
import { Check } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * Native HTML Checkbox implementation
 * Uses native input[type="checkbox"] to avoid Radix UI state management issues
 * that can cause React Error #185 (Maximum update depth exceeded)
 */
interface CheckboxProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  id?: string;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
}

const Checkbox = React.forwardRef<HTMLButtonElement, CheckboxProps>(
  ({ checked, defaultChecked, onCheckedChange, disabled, id, className, onClick, ...props }, ref) => {
    // Use internal state for uncontrolled mode
    const [internalChecked, setInternalChecked] = React.useState(defaultChecked ?? false);

    // Determine if controlled or uncontrolled
    const isControlled = checked !== undefined;
    const isChecked = isControlled ? checked : internalChecked;

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;

      // Call custom onClick handler if provided
      if (onClick) {
        onClick(e);
        // If event was stopped, don't toggle
        if (e.defaultPrevented) return;
      }

      const newValue = !isChecked;

      // Update internal state for uncontrolled mode
      if (!isControlled) {
        setInternalChecked(newValue);
      }

      // Notify parent
      if (onCheckedChange) {
        onCheckedChange(newValue);
      }
    };

    return (
      <button
        ref={ref}
        type="button"
        role="checkbox"
        aria-checked={isChecked}
        id={id}
        disabled={disabled}
        onClick={handleClick}
        className={cn(
          "peer h-4 w-4 shrink-0 rounded-sm border border-gray-300 ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 flex items-center justify-center transition-colors",
          isChecked ? "bg-teal-600 text-white border-teal-600" : "bg-white",
          className
        )}
        {...props}
      >
        {isChecked && <Check className="h-3 w-3" strokeWidth={3} />}
      </button>
    );
  }
);

Checkbox.displayName = "Checkbox"

export { Checkbox }
